import { ColumnDataType, Provider, WorkflowComponentParamType, WorkflowComponentCodeParams } from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

const MILES_TO_KM = 1.60934

class TradeAreas extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'alteryx.tradeareas',
        group: 'Spatial Operations',
        title: 'Trade Areas',
        description: `This component creates regions around point objects in the input table.`,
        tags: ['alteryx']
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'spatialfield',
          type: WorkflowComponentParamType.Column,
          title: 'Spatial Object Field of Point Source',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'radius',
          type: WorkflowComponentParamType.String,
          title: 'Radius, Doughnuts',
          allowExpressions: false
        },
        {
          name: 'units',
          type: WorkflowComponentParamType.Selection,
          title: 'Units',
          options: ['Kilometers', 'Miles'],
          default: 'Kilometers'
        },
        {
          name: 'include',
          type: WorkflowComponentParamType.Boolean,
          title: 'Include Point in Output',
          default: true
        },
        {
          name: 'nooverlap',
          type: WorkflowComponentParamType.Boolean,
          title: 'Eliminate Overlap',
          default: false
        }
      ]
    )
  }

  getInnerSql({ params, connection, envVars }: WorkflowComponentCodeParams) {
    if (connection.type !== Provider.BigQuery) {
      return null
    }
    const distanceConversion = params.units === 'Kilometers' ? ` * 1000` : ` * 1000 * ${MILES_TO_KM}`
    if (params.nooverlap && !isNaN(params.radius as number)) {
      return `WITH __voronoi AS(
            SELECT __source.*, voronoi
            FROM (
              SELECT
                \`${envVars.analyticsToolboxDataset}.ST_VORONOIPOLYGONS\`(pts, NULL) polygons
              FROM (
                SELECT ARRAY_AGG(ST_CENTROID(${params.spatialfield})) pts
                FROM ${connection.quoteName(params.source as string)}
              )
            ) triangulation, UNNEST(triangulation.polygons) voronoi
            JOIN ${connection.quoteName(params.source as string)} __source
            ON ST_CONTAINS(voronoi, __source.${params.spatialfield})
          )
          SELECT ST_INTERSECTION(
            ST_BUFFER(
              ${params.spatialfield},
              ${params.radius} ${distanceConversion}),
              voronoi
            ) ${params.spatialfield}_trade_area, * EXCEPT(voronoi)
            FROM __voronoi`
    } else {
      const radiuses = ((params.radius as string) ?? '').split(',').map((x) => x.trim())
      const arrayToUnnest = radiuses
        .map((x) => {
          const tokens = x.split('-')
          if (tokens[1]) {
            return `STRUCT (${tokens[0]} ${distanceConversion} AS a, ${tokens[1]} ${distanceConversion} AS b)`
          } else {
            return `STRUCT (0 AS a, ${tokens[0]} ${distanceConversion} AS b)`
          }
        })
        .join(', ')
      return `WITH dists AS (
                SELECT
                  s.${params.spatialfield} __geom,
                  ST_ASTEXT(s.${params.spatialfield}) __geotxt,
                  ST_CENTROID(s.${params.spatialfield}) __pt,
                  __dists.*
                FROM
                  ${connection.quoteName(params.source as string)} s,
                  UNNEST([${arrayToUnnest}]) __dists
              ),
              areas AS (
                SELECT
                  ST_DIFFERENCE(
                    ST_BUFFER(__geom, b),
                    ST_BUFFER(__geom,a)
                  ) ${params.spatialfield}_trade_area,
                  __geotxt, __pt,
                  CONCAT(a, ',', b) radius_size
                FROM dists
              )
              SELECT
                ${params.spatialfield}_trade_area,
                radius_size,
                s.* ${params.include ? ', __pt centroid' : ''}
              FROM areas
              JOIN ${connection.quoteName(params.source as string)} s
              ON ST_ASTEXT(s.${params.spatialfield}) = __geotxt`
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery]
  }
}

export { TradeAreas }
